import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';

export default function MyBookingsPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [bookings, setBookings] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/bookings');
      const mine = (res.data.data || []).filter(
        b => (b.contactInfo?.email || '').toLowerCase() === email.toLowerCase()
      );
      setBookings(mine.map(b => ({
        bookingId: b._id,
        pnr: b.bookingReference,
        status: (b.bookingStatus || 'confirmed').toUpperCase(),
        totalAmount: b.totalPrice,
        passengers: b.passengerDetails || [],
        flight: b.flightDetails || {},
      })));
      setSearched(true);
    } catch {
      setError('Failed to fetch bookings.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setCancellingId(bookingId);
    try {
      await axios.put(`/api/bookings/${bookingId}/cancel`);
      setBookings(prev => prev.map(b => b.bookingId === bookingId ? { ...b, status: 'CANCELLED' } : b));
    } catch {
      alert('Failed to cancel booking.');
    } finally {
      setCancellingId(null);
    }
  };

  const statusColor = (status) => {
    if (status === 'CONFIRMED') return 'bg-green-100 text-green-700';
    if (status === 'CANCELLED') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <SEO
        title="My Bookings"
        canonical="/my-bookings"
        description="View and manage your flight bookings on Flyairlinebooking. Enter your email to retrieve your booking history, PNR and e-tickets."
        keywords="my bookings, manage flight booking, view reservation, PNR lookup, flight confirmation"
        noindex={true}
      />
      <h1 className="text-2xl font-bold text-gray-800 mb-6">📋 My Bookings</h1>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="bg-white rounded-xl shadow p-5 mb-8 border border-gray-100">
        <label className="block text-sm font-medium text-gray-700 mb-2">Enter your email to find bookings</label>
        <div className="flex gap-3">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            className="flex-grow border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="your@email.com"
          />
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2.5 rounded-lg font-semibold transition-colors"
          >
            {loading ? 'Searching...' : 'Find'}
          </button>
        </div>
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </form>

      {/* Results */}
      {searched && (
        <>
          {bookings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-5xl mb-3">🔍</div>
              <p>No bookings found for <strong>{email}</strong>.</p>
              <button onClick={() => navigate('/')} className="mt-4 text-blue-600 underline">Search for flights</button>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map(booking => (
                <div key={booking.bookingId} className="bg-white rounded-xl shadow p-5 border border-gray-100">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-bold text-gray-800 text-lg">
                          {booking.flight.originCity} → {booking.flight.destinationCity}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm">{booking.flight.airline} • {booking.flight.flightNumber} • {booking.flight.date}</p>
                      <p className="text-gray-600 text-sm">{booking.flight.departureTime} – {booking.flight.arrivalTime} • {booking.flight.duration}</p>
                      <div className="mt-2 flex gap-4 text-xs text-gray-500">
                        <span>Booking ID: <strong>{booking.bookingId}</strong></span>
                        <span>PNR: <strong className="tracking-widest">{booking.pnr}</strong></span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-between gap-2">
                      <p className="text-xl font-bold text-blue-600">${booking.totalAmount?.toLocaleString('en-US')}</p>
                      <p className="text-xs text-gray-400">{booking.passengers?.length} passenger{booking.passengers?.length > 1 ? 's' : ''}</p>
                      {booking.status === 'CONFIRMED' && (
                        <button
                          onClick={() => handleCancel(booking.bookingId)}
                          disabled={cancellingId === booking.bookingId}
                          className="text-sm text-red-500 hover:text-red-700 border border-red-300 px-3 py-1 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {cancellingId === booking.bookingId ? 'Cancelling...' : 'Cancel Booking'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Passengers */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Passengers:</p>
                    <div className="flex flex-wrap gap-2">
                      {booking.passengers?.map((p, i) => (
                        <span key={i} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs">
                          {p.firstName} {p.lastName}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
